import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Eye, BellRing, Clock, Radio, UserCheck, Video, Users, Flame, HeartPulse,
  Home as HomeIcon, Store, Building2, Warehouse, ArrowRight, Phone, CheckCircle2, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LeadForm from "@/components/LeadForm";
import Plans from "@/components/Plans";
import GoogleReviews from "@/components/GoogleReviews";
import { BRAND, telLink, waLink, monitoringTelLink } from "@/lib/brand";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

const HERO_BG =
  "https://images.unsplash.com/photo-1708807472445-d33589e6b090?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxzdXJ2ZWlsbGFuY2UlMjBvcGVyYXRvciUyMHNjcmVlbnxlbnwwfHx8fDE3ODM4MzY0Mzd8MA&ixlib=rb-4.1.0&q=85";

const CAM_IMG =
  "https://images.unsplash.com/photo-1618482914248-29272d021005?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxjY3R2JTIwY2FtZXJhJTIwc2VjdXJpdHl8ZW58MHx8fHwxNzgzODM2NDIxfDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  return (
    <div data-testid="home-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/70 to-[#0A0A0B]" />
          <div className="absolute inset-0 grid-bg opacity-40" />
        </div>

        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 pt-14 pb-24 lg:pt-24 lg:pb-32 grid gap-12 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7">
            <motion.div {...fadeUp} className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border-2 border-brand-cyan/60 bg-brand-cyan/15 text-brand-cyan text-sm sm:text-base font-bold shadow-[0_0_25px_rgba(0,229,255,0.25)]">
              <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="tracking-wide">First of its kind in Guwahati, Assam</span>
              <span className="ml-1 h-2 w-2 rounded-full bg-brand-cyan pulse-dot" />
            </motion.div>

            <motion.h1
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.05 }}
              data-testid="hero-heading"
              className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05]"
            >
              We watch your <span className="text-brand-cyan text-glow">CCTV</span><br />
              so <span className="italic font-medium text-white/90">nothing</span> gets past you.
            </motion.h1>

            <motion.p
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="mt-6 text-white/70 text-base sm:text-lg max-w-2xl"
            >
              Secure Watch is Guwahati's first dedicated <strong className="text-white">online remote monitoring</strong> service.
              Our command center watches your cameras every night from 10 PM to 6 AM, spots threats in real-time and
              alerts you — before anything goes wrong.
            </motion.p>

            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.15 }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <Link to="/contact" data-testid="hero-cta-book">
                <Button className="h-12 px-6 bg-brand-cyan text-black hover:bg-brand-cyan/90 font-bold shadow-[0_0_30px_rgba(0,229,255,0.35)] hover:-translate-y-0.5 transition-transform">
                  Book a FREE Security Audit
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href={telLink} data-testid="hero-cta-call">
                <Button variant="outline" className="h-12 px-6 border-white/20 text-white hover:bg-white/10 font-semibold">
                  <Phone className="mr-2 h-4 w-4 text-brand-cyan" />
                  Call {BRAND.phoneDisplay}
                </Button>
              </a>
            </motion.div>

            <motion.a
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.17 }}
              href={monitoringTelLink}
              data-testid="hero-monitoring-phone"
              className="mt-4 inline-flex items-center gap-2 text-xs sm:text-sm text-white/60 hover:text-brand-cyan"
            >
              <span className="inline-flex h-5 items-center gap-1 rounded-full bg-red-500/15 border border-red-500/40 text-red-400 px-2 text-[10px] uppercase tracking-[0.18em] font-bold">
                <span className="h-1.5 w-1.5 rounded-full bg-red-500 pulse-dot" /> Monitoring
              </span>
              <span>Command room hotline · <strong className="text-white">{BRAND.monitoringPhoneDisplay}</strong></span>
            </motion.a>

            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm">
              {["Nightly 10 PM – 6 AM watch", "Instant threat alerts", "Instant alarm activation", "No hidden charges", "Assam-based team"].map((t) => (
                <span key={t} className="flex items-center gap-2 text-white/70">
                  <CheckCircle2 className="h-4 w-4 text-brand-cyan" /> {t}
                </span>
              ))}
            </motion.div>
          </div>

          {/* Holiday Watch Ad (hero card) */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.25 }}
            data-testid="holiday-hero-ad"
            className="lg:col-span-5"
          >
            <div className="relative overflow-hidden rounded-2xl border border-brand-cyan/40 bg-[#0F0F13] shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
              {/* Feature image */}
              <div className="relative h-52 sm:h-60 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1523987355523-c7b5b0dd90a7?w=1400&q=80&auto=format&fit=crop"
                  alt="Family enjoying a holiday while Secure Watch monitors their home"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F13] via-[#0F0F13]/40 to-transparent" />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-brand-cyan/50 bg-black/70 backdrop-blur-md text-brand-cyan text-[11px] font-bold uppercase tracking-[0.18em]">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-cyan pulse-dot" />
                  Holiday Watch
                </div>
                <div className="absolute top-4 right-4 text-[11px] font-mono text-white/70 bg-black/60 backdrop-blur px-2 py-0.5 rounded">
                  NEW · GUWAHATI
                </div>
              </div>

              <div className="relative p-6 sm:p-7">
                <h3 className="font-heading text-2xl sm:text-3xl font-extrabold text-white leading-tight">
                  Going on holiday? <span className="text-brand-cyan text-glow">We'll watch the house.</span>
                </h3>

                <p className="mt-3 text-white/70 text-sm">
                  Flexible CCTV monitoring by the <strong className="text-white">day, week or month</strong> —
                  trained live operators watch your home while you're away, for complete peace of mind.
                </p>

                <div className="mt-5 grid grid-cols-2 gap-2">
                  {["Daily plans", "Weekly plans", "Monthly plans", "Instant activation"].map((f) => (
                    <div key={f} className="flex items-center gap-1.5 text-xs text-white/80">
                      <CheckCircle2 className="h-3.5 w-3.5 text-brand-cyan shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex flex-col sm:flex-row gap-2">
                  <a href={telLink} data-testid="holiday-hero-cta-call" className="flex-1">
                    <Button className="w-full h-11 bg-brand-cyan text-black hover:bg-brand-cyan/90 font-bold shadow-[0_0_20px_rgba(0,229,255,0.35)]">
                      <Phone className="mr-2 h-4 w-4" />
                      Call for quote
                    </Button>
                  </a>
                  <a
                    href={waLink("Hi Secure Watch, I'd like a quote for the Holiday Watch plan — please share details.")}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="holiday-hero-cta-wa"
                    className="flex-1"
                  >
                    <Button variant="outline" className="w-full h-11 border-white/25 bg-black/40 text-white hover:bg-white/10 font-semibold">
                      WhatsApp
                    </Button>
                  </a>
                </div>

                <p className="mt-4 text-[11px] text-brand-cyan/90">
                  Book an appointment & get a <strong className="text-white">FREE site survey</strong> — we start watching from the very next night.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-y border-white/10 bg-[#0C0C0F]">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { k: "10PM-6AM", v: "Live Monitoring" },
            { k: "<60s", v: "Alert Response" },
            { k: "500+", v: "Cameras Watched" },
            { k: "1st", v: "In Guwahati" },
          ].map((s) => (
            <div key={s.k} className="text-center border-l first:border-l-0 border-white/10 pl-6 md:pl-0">
              <div className="font-heading text-3xl sm:text-4xl font-extrabold text-brand-cyan text-glow">{s.k}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/50 mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Fire Prevention banner (compact) */}
      <section id="fire" className="relative">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 pt-10 lg:pt-14">
          <motion.div
            {...fadeUp}
            data-testid="fire-prevention-banner"
            className="relative overflow-hidden rounded-2xl border border-orange-500/40 bg-[#0F0F13]"
          >
            <div className="grid lg:grid-cols-12">
              {/* Image column */}
              <div className="relative lg:col-span-5 h-56 lg:h-auto min-h-[320px] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_cctv-leads/artifacts/hnlpvtj3_warehouse-fires-structual-damage.jpg"
                  alt="A warehouse fire — the disaster Secure Watch fire prevention monitoring helps you avoid"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/20 to-[#0F0F13] lg:to-[#0F0F13]" />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-orange-500/60 bg-black/70 backdrop-blur-md text-orange-300 text-[11px] font-bold uppercase tracking-[0.18em]">
                  <Flame className="h-3 w-3" />
                  Fire Safety Watch · Guwahati
                </div>
                <div className="absolute bottom-4 left-4 right-4 lg:right-8">
                  <div className="inline-flex items-center gap-2 rounded-lg bg-black/75 backdrop-blur-md border border-red-500/40 px-3 py-2 text-white text-xs">
                    <span className="text-red-400 font-bold uppercase tracking-wider text-[10px]">Don't let this happen.</span>
                  </div>
                </div>
              </div>

              {/* Content column */}
              <div className="relative lg:col-span-7 p-6 sm:p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-8">
                <div className="flex-1">
                  <h3 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight">
                    Secured remote monitoring for <span className="text-orange-400">fire prevention</span>.
                  </h3>

                  <p className="mt-3 text-white/70 text-sm sm:text-base max-w-2xl">
                    Round-the-clock human watch on your fire alarms, smoke detectors &amp; sprinkler feeds.
                    The second something triggers — we call you, your team &amp; the fire station.
                  </p>

                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {["Fire alarm alerts", "Smoke detection", "Sprinkler check", "Instant escalation", "24×7 secured"].map((k) => (
                      <span
                        key={k}
                        data-testid={`fire-tag-${k.toLowerCase().replace(/\s|×/g, "-")}`}
                        className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-black/40 px-2.5 py-1 text-[11px] text-white/80"
                      >
                        <Flame className="h-3 w-3 text-orange-400" />
                        {k}
                      </span>
                    ))}
                  </div>

                  <p className="mt-4 text-[11px] text-orange-300/90">
                    Book an appointment and get a <strong>FREE site survey</strong> — we start watching from the very next night.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row lg:flex-col gap-2.5 lg:min-w-[200px]">
                  <a href={telLink} data-testid="fire-cta-call" className="w-full">
                    <Button className="w-full h-11 bg-orange-500 text-black hover:bg-orange-400 font-bold shadow-[0_0_20px_rgba(249,115,22,0.4)]">
                      <Phone className="mr-2 h-4 w-4" />
                      Call for quote
                    </Button>
                  </a>
                  <a
                    href={waLink("Hi Secure Watch, I want a quote for your Fire Prevention Remote Monitoring service.")}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="fire-cta-wa"
                    className="w-full"
                  >
                    <Button variant="outline" className="w-full h-11 border-white/25 bg-black/40 text-white hover:bg-white/10 font-semibold">
                      WhatsApp for quote
                    </Button>
                  </a>
                  <Link to="/contact" data-testid="fire-cta-book" className="w-full">
                    <Button variant="ghost" className="w-full h-9 text-orange-300 hover:text-orange-300 hover:bg-orange-500/10 text-xs font-semibold">
                      Book free site survey
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Senior Citizens SOS Care Promo */}
      <section id="senior-care" className="relative">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 pt-8 lg:pt-12">
          <motion.div
            {...fadeUp}
            data-testid="senior-care-banner"
            className="relative overflow-hidden rounded-2xl border border-rose-400/50 bg-gradient-to-br from-rose-500/20 via-[#12080c] to-[#0F0F13] shadow-[0_25px_60px_rgba(244,63,94,0.15)]"
          >
            <div className="grid lg:grid-cols-12">
              {/* Image column */}
              <div className="relative lg:col-span-5 h-64 lg:h-auto min-h-[320px] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1514415008039-efa173293080?w=1400&q=80&auto=format&fit=crop"
                  alt="Happy elderly couple safe at home with Secure Watch"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/10 to-[#12080c] lg:to-[#12080c]" />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full border-2 border-rose-400/70 bg-black/70 backdrop-blur-md px-3 py-1.5 text-rose-300 text-xs sm:text-sm font-bold uppercase tracking-[0.18em] shadow-[0_0_20px_rgba(244,63,94,0.35)]">
                  <HeartPulse className="h-4 w-4" />
                  New · Senior Care
                  <span className="mx-1 h-1 w-1 rounded-full bg-rose-300" />
                  SOS Medical Ready
                  <span className="ml-1 h-2 w-2 rounded-full bg-rose-400 pulse-dot" />
                </div>
              </div>

              {/* Content column */}
              <div className="relative lg:col-span-7 p-6 sm:p-8 lg:p-10 flex flex-col justify-center">
                <h3 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight">
                  Elderly parents at home?<br />
                  <span className="text-rose-300">We watch over them, day &amp; night.</span>
                </h3>
                <p className="mt-4 text-white/80 text-sm sm:text-base max-w-2xl">
                  Special safety &amp; monitoring plans for senior citizens — with a{" "}
                  <strong className="text-rose-300">one-tap SOS medical emergency button</strong>. The
                  moment they need help, we instantly alert you, nearby family, neighbours and dispatch
                  an ambulance to your doorstep.
                </p>

                <div className="mt-5 flex flex-wrap gap-2">
                  {[
                    "One-tap SOS button",
                    "Family + ambulance alert",
                    "Fall detection ready",
                    "24×7 wellness check-ins",
                  ].map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center gap-1.5 rounded-full border border-rose-400/30 bg-rose-500/10 px-3 py-1 text-[11px] text-rose-100 font-medium"
                    >
                      <HeartPulse className="h-3 w-3 text-rose-400" />
                      {t}
                    </span>
                  ))}
                </div>

                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <a
                    href={waLink("Hi Secure Watch, I want details of your Senior Citizen Safety + SOS Medical plan.")}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="senior-care-cta-wa"
                  >
                    <Button className="h-11 bg-rose-500 hover:bg-rose-400 text-white font-bold shadow-[0_0_25px_rgba(244,63,94,0.45)]">
                      Enquire on WhatsApp
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </a>
                  <a href={telLink} data-testid="senior-care-cta-call">
                    <Button variant="outline" className="h-11 border-rose-300/40 text-rose-100 hover:bg-rose-500/10 font-semibold">
                      <Phone className="mr-2 h-4 w-4" />
                      Call for details
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="relative mx-auto max-w-7xl px-5 lg:px-8 py-20 lg:py-28">
        <motion.div {...fadeUp}>
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-cyan">How it works</span>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white max-w-3xl">
            You already own CCTV cameras.<br />We turn them into a real security system.
          </h2>
        </motion.div>

        <div className="mt-14 grid gap-6 md:grid-cols-8 lg:grid-cols-12">
          {/* Big card */}
          <motion.div {...fadeUp} className="md:col-span-8 lg:col-span-7 relative overflow-hidden rounded-2xl border border-brand-cyan/30 bg-gradient-to-br from-brand-cyan/10 to-transparent p-8 lg:p-10">
            <div className="flex items-center gap-2 text-brand-cyan">
              <Eye className="h-5 w-5" />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold">Step 01 · Watch</span>
            </div>
            <h3 className="font-heading text-2xl lg:text-3xl font-bold text-white mt-3">
              Our trained operators watch your live feeds from our Guwahati command center.
            </h3>
            <p className="text-white/70 mt-4 max-w-xl">
              We securely connect to your existing DVR/NVR — no new hardware needed in most cases. Your
              cameras become "eyes" that never blink, never take a coffee break.
            </p>
            <div className="mt-6 flex items-center gap-3 text-sm text-white/60">
              <Radio className="h-4 w-4 text-brand-cyan" /> Encrypted stream · Local storage stays yours
            </div>
            <div className="absolute -right-6 -bottom-6 h-48 w-48 rounded-full bg-brand-cyan/10 blur-3xl" />
          </motion.div>

          <motion.div {...fadeUp} className="md:col-span-4 lg:col-span-5 rounded-2xl border border-white/10 bg-[#0F0F13] p-8 relative overflow-hidden">
            <img src={CAM_IMG} alt="CCTV camera" className="absolute inset-0 h-full w-full object-cover opacity-25" />
            <div className="relative">
              <div className="flex items-center gap-2 text-brand-cyan">
                <UserCheck className="h-5 w-5" />
                <span className="text-xs uppercase tracking-[0.2em] font-semibold">Step 02 · Verify</span>
              </div>
              <h3 className="font-heading text-xl lg:text-2xl font-bold text-white mt-3">
                Real people verifying, in real time
              </h3>
              <p className="text-white/70 mt-3 text-sm">
                Every suspicious movement is verified by a trained live operator — never a bot. No more
                false alarms from stray cats or a gust of wind.
              </p>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="md:col-span-4 lg:col-span-4 rounded-2xl border border-white/10 bg-[#0F0F13] p-8">
            <div className="flex items-center gap-2 text-brand-cyan">
              <BellRing className="h-5 w-5" />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold">Step 03 · Alert</span>
            </div>
            <h3 className="font-heading text-xl font-bold text-white mt-3">
              Instant call + Activate Alarm & local police
            </h3>
            <p className="text-white/70 mt-3 text-sm">
              Within seconds of a real threat, we trigger your on-site alarm, ring you and — if you
              allow — nearby police / your security guard, with the incident clip.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="md:col-span-4 lg:col-span-4 rounded-2xl border border-white/10 bg-[#0F0F13] p-8">
            <div className="flex items-center gap-2 text-brand-cyan">
              <Video className="h-5 w-5" />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold">Step 04 · Record</span>
            </div>
            <h3 className="font-heading text-xl font-bold text-white mt-3">
              Daily activity report & incident clips
            </h3>
            <p className="text-white/70 mt-3 text-sm">
              Get a clean daily summary of visitors, deliveries, staff attendance and any incidents —
              straight to your phone.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="md:col-span-4 lg:col-span-4 rounded-2xl border border-white/10 bg-[#0F0F13] p-8">
            <div className="flex items-center gap-2 text-brand-cyan">
              <ShieldCheck className="h-5 w-5" />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold">Step 05 · Protect</span>
            </div>
            <h3 className="font-heading text-xl font-bold text-white mt-3">
              You sleep. We stay awake.
            </h3>
            <p className="text-white/70 mt-3 text-sm">
              Whether it's your shop after 10pm or your godown on a Sunday — Secure Watch never closes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="relative border-t border-white/10 bg-[#0B0B0E] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <motion.div {...fadeUp} className="max-w-3xl">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-cyan">What we monitor</span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">
              Trusted by homes, shops & businesses across Guwahati.
            </h2>
          </motion.div>

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: HomeIcon, title: "Homes & Villas", desc: "Peace of mind while you're at work, travelling or asleep." },
              { icon: Store, title: "Shops & Retail", desc: "Watch cash counters, staff & shutters after hours." },
              { icon: Building2, title: "Offices", desc: "Track staff punctuality & unauthorised entry after work." },
              { icon: Warehouse, title: "Warehouses & Godowns", desc: "Nightly vigilance on stock, entry gates & loading bays." },
            ].map((s, i) => (
              <motion.div
                key={s.title}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.05 }}
                data-testid={`service-card-${s.title.toLowerCase().replace(/\s|&/g, "-")}`}
                className="group relative rounded-2xl border border-white/10 bg-[#0F0F13] p-6 hover:border-brand-cyan/50 transition-colors"
              >
                <div className="h-11 w-11 rounded-lg bg-brand-cyan/10 border border-brand-cyan/30 flex items-center justify-center text-brand-cyan">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="font-heading text-lg font-bold text-white mt-5">{s.title}</h3>
                <p className="text-white/60 text-sm mt-2">{s.desc}</p>
                <div className="mt-5 flex items-center gap-1 text-xs text-brand-cyan/80 group-hover:text-brand-cyan">
                  Get audit <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans / Pricing */}
      <Plans />

      {/* Testimonials → Google Reviews */}
      <GoogleReviews />


      {/* FAQ */}
      <section id="faq" className="border-t border-white/10 bg-[#0B0B0E] py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <motion.div {...fadeUp} className="text-center">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-cyan">FAQ</span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">
              Everything you want to know
            </h2>
          </motion.div>

          <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
            {[
              { q: "Do I need to buy new cameras?", a: "In most cases, no. We can connect to your existing DVR/NVR with a small one-time setup. If your current CCTV is very old, our team will recommend an upgrade during the free audit." },
              { q: "How is this different from just having CCTV?", a: "CCTV only records — it doesn't stop crime. Secure Watch actively watches your feeds in real time, so incidents are caught & stopped before they escalate." },
              { q: "Is my footage safe & private?", a: "Yes. Streams are encrypted and only authorised operators can view them. Recordings continue to stay on your local DVR/NVR — we do not take copies unless you ask us to." },
              { q: "Where do you provide service?", a: "We currently cover all of Guwahati & Kamrup Metro. We're also rolling out in North Guwahati, Sonapur, Sivasagar and neighbouring towns in Assam." },
              { q: "How much does it cost?", a: "It depends on the number of cameras and monitoring hours you need. Plans start well under what a night guard costs. Book a free audit and we'll build a custom plan." },
            ].map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-white hover:text-brand-cyan hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-white/70">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="mx-auto max-w-6xl px-5 lg:px-8 py-20 lg:py-24 text-center relative">
          <motion.div {...fadeUp} className="mx-auto max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-brand-cyan/40 bg-brand-cyan/10 text-brand-cyan text-xs font-semibold">
              <Users className="h-3.5 w-3.5" />
              Limited free audits this month
            </div>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-6xl font-extrabold text-white">
              Turn your CCTV into <span className="text-brand-cyan text-glow">real security</span> today.
            </h2>
            <p className="mt-5 text-white/70 text-lg">
              Book a free 30-minute security audit at your home or business — anywhere in Guwahati.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact" data-testid="final-cta-book">
                <Button className="h-12 px-6 bg-brand-cyan text-black hover:bg-brand-cyan/90 font-bold shadow-[0_0_30px_rgba(0,229,255,0.35)] hover:-translate-y-0.5 transition-transform">
                  Book my free audit
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href={waLink()} target="_blank" rel="noreferrer noopener" data-testid="final-cta-wa">
                <Button variant="outline" className="h-12 px-6 border-white/20 text-white hover:bg-white/10 font-semibold">
                  Chat on WhatsApp
                </Button>
              </a>
            </div>
            <div className="mt-6 text-xs text-white/40 flex items-center justify-center gap-2">
              <Clock className="h-3.5 w-3.5 text-brand-cyan" /> Response within 30 minutes · 7 days a week
            </div>
          </motion.div>
        </div>
      </section>

      {/* Inline mini-form for direct capture */}
      <section className="mx-auto max-w-4xl px-5 lg:px-8 pb-24">
        <LeadForm />
      </section>

      <Footer />
    </div>
  );
}
